import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchHistoricalReadings } from "../actions/liveMonitorActions";
import ReadingDetailsModal from "../components/live_monitor/ReadingDetailsModal";
import { Eye } from "lucide-react";

function ViewHistoryPage () {
    const { deviceId } = useParams();
    const dispatch = useDispatch();

    const { historicalReadings, historyLoading, error } = useSelector(
        (state) => state.liveMonitor
    );

    const [selectedReading, setSelectedReading] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [timeMode, setTimeMode] = useState("after");
    const [page, setPage] = useState(1);

    const PAGE_SIZE = 25;
    const inputClass = "w-full py-2 md:px-4 md:py-3 rounded-xl border border-border bg-white focus:border-electricBlue focus:ring-4 focus:ring-electricBlue/10 outline-none";

    useEffect(() => {
        dispatch(fetchHistoricalReadings());

        const interval = setInterval(() => {
            dispatch(fetchHistoricalReadings());
        }, 60000);

        return () => clearInterval(interval);
    }, [dispatch]);

    const readings = useMemo(() => {
        // return historicalReadings
        //     .filter((r) => r.DeviceId === deviceId)
        //     .sort((a, b) => new Date(`${b.Ddate} ${b.Dtime}`) - new Date(`${a.Ddate} ${a.Dtime}`))

        let filtered = historicalReadings.filter(
            r => r.DeviceId === deviceId
        );

        if (selectedDate) {
            filtered = filtered.filter(
                r => r.Ddate === selectedDate
            );
        }

        if (selectedTime) {
            filtered = filtered.filter(r => {
                if (timeMode === "after") {
                    return r.Dtime >= selectedTime
                }

                return r.Dtime <= selectedTime;
            })
        }

        return filtered.sort(
            (a, b) => new Date(`${b.Ddate} ${b.Dtime}`) - new Date(`${a.Ddate} ${a.Dtime}`)
        )
    }, [historicalReadings, deviceId, selectedDate, selectedTime, timeMode])

    const latestReading = readings[0];

    // Data needed for pagination
    const totalPages = Math.ceil(readings.length / PAGE_SIZE)
    const paginatedReadings = readings.slice( (page - 1) * PAGE_SIZE, page * PAGE_SIZE );

    const startReading = readings.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endReading = Math.min(page * PAGE_SIZE, readings.length)

    // Reset pagination when data changes
    useEffect(() => {
        setPage(1);
    }, [selectedDate, selectedTime, timeMode])

    return (
        <div className="w-full px-4 py-8 md:p-10 flex flex-col gap-6">
            
            <div className="mb-2">

                <p className="text-sm uppercase tracking-[4px] text-electricBlue font-semibold">
                    Historical Analysis
                </p>

                <h1 className="mt-2 text-3xl md:text-4xl font-bold text-textPrimary">
                    Reading History
                </h1>

                <p className="mt-2 text-sm md:text-base text-textSecondary">
                    Historical readings and operational metrics for {deviceId}
                </p>
            </div>

            {/* Device summary card */}
            <div className="bg-white border border-border rounded-3xl p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-bold">
                            {deviceId}
                        </h2>

                        <p className="text-textSecondary">
                            {latestReading?.DiviceName || "Unknown Device"}
                        </p>
                    </div>

                    <div className="w-fit px-4 py-2 rounded-xl bg-electricBlue/10 text-electricBlue font-semibold flex items-center">
                        {latestReading?.DiviceType === "G" ? "Generator" : latestReading?.DiviceType === "P" ? "Power Panel" : "Unknown"}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-border rounded-3xl p-4 md:p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-textPrimary">
                        Filters
                    </h3>

                    <button
                        onClick={() => {
                            setSelectedDate("");
                            setSelectedTime("");
                            setTimeMode("after");
                        }}
                        className="text-sm text-electricBlue hover:text-blue-700"
                    >
                        Clear Filters
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                    <div>
                        <label className="block text-sm font-medium text-textSecondary mb-2">
                            Date
                        </label>

                        <input 
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-textSecondary mb-2">
                            Time
                        </label>

                        <input 
                            type="time"
                            value={selectedTime}
                            onChange={(e) => setSelectedTime(e.target.value)}
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-textSecondary mb-2">
                            Filter Mode
                        </label>

                        <div className="flex rounded-xl bg-surfaceSoft p-1 border border-border">
                            <button
                                onClick={() => setTimeMode("before")}
                                className={`
                                    flex-1 py-3 rounded-lg transition
                                    ${timeMode === "before" ? "bg-deepNavy text-white" : "text-slate-500"}  
                                `}
                            >
                                Before
                            </button>

                            <button
                                onClick={() => setTimeMode("after")}
                                className={`
                                    flex-1 py-3 rounded-lg transition
                                    ${timeMode === "after" ? "bg-deepNavy text-white" : "text-slate-500"}  
                                `}
                            >
                                After
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 md:px-6 py-4 border-b border-border flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                <p className="text-sm text-textSecondary">
                    Showing {startReading} - {endReading} of {readings.length} readings
                </p>

                <p className="text-sm text-textSecondary">
                    Page {page} of {totalPages || 1}
                </p>
            </div>

            <div className="bg-white rounded-3xl border border-border overflow-x-auto max-w-full">
                { historyLoading ? (
                    <p className="p-8 text-center text-slate-500">Loading readings...</p>
                ) : !historyLoading && paginatedReadings.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                        No readings found for the selected filters.
                    </div>
                ) : (
                    <table className="w-full min-w-[1000px]">
                        <thead className="bg-surfaceSoft">
                            <tr>
                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Date</th>
                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Time</th>
                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Apparent Power (kVA)</th>
                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Power Factor</th>
                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Frequency (Hz)</th>
                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Active Power</th>
                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Active Energy</th>
                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginatedReadings.map((reading, index) => (
                                <>
                                    <tr
                                        key={`${reading.DeviceId}-${index}`}
                                        className="border-t border-border cursor-pointer hover:bg-slate-50 transition"
                                        // onClick={() => setSelectedReading(reading)}
                                    >
                                        <td className="p-4 text-center whitespace-nowrap">{reading.Ddate}</td>
                                        <td className="p-4 text-center whitespace-nowrap">{reading.Dtime}</td>
                                        <td className="p-4 text-center">{reading.TotalApparentPowerKVA}</td>
                                        <td className="p-4 text-center">{reading.PowerFactor}</td>
                                        <td className="p-4 text-center">{reading.FrequencyHz}</td>
                                        <td className="p-4 text-center">{reading.TotalActivePowerKW}</td>
                                        <td className="p-4 text-center">{reading.ActiveEnergyKWh}</td>
                                        <td className="p-4 text-center">
                                            <button
                                                type="button"
                                                title="View Details"
                                                onClick={() => setSelectedReading(reading)}
                                                className="p-2 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-electricBlue hover:bg-electricBlue/10 transition"
                                            >
                                                <Eye size={14} />
                                            </button>
                                        </td>
                                    </tr>

                                </>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Paginated Controls */}
            <div className="flex flex-col md:flex-row justify-end gap-3 p-4 md:p-6 border-t border-border">
                <button
                    disabled={page === 1}
                    onClick={() => setPage(prev => prev - 1)}
                    className="w-full md:w-auto px-4 py-2 rounded-xl border border-border disabled:opacity-50"
                >
                    Previous
                </button>

                <div className="w-full md:w-auto text-center px-4 py-2 rounded-xl bg-deepNavy text-white">
                    {page}
                </div>

                <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(prev => prev + 1)}
                    className="w-full md:w-auto px-4 py-2 rounded-xl border border-border disabled:opacity-50"
                >
                    Next
                </button>
            </div>

            {selectedReading && (
                <ReadingDetailsModal 
                    reading={selectedReading}
                    onClose={() => setSelectedReading(null)}
                />
            )}
        </div>
    )
}

export default ViewHistoryPage;